import { apiClient } from '../../utils/Api';
import { initialReturnQcForm, returnQcPayload } from './returnQcForm';
import { readSaleReturn } from './returnSale';

const toCents = (value) => {
  const text = String(value == null ? '' : value).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  return Number(whole) * 100 + Number(`${fraction}00`.slice(0, 2));
};

export const relistAskingPriceFromLowestCents = (lowestCents) => {
  if (lowestCents == null) return '0';
  const next = lowestCents - 1000000;
  if (next <= 0) return '0';
  return String(Math.floor(next / 100));
};

export const lowestAskCents = (rows) => {
  let lowest = null;
  (rows || []).forEach((row) => {
    const ask = toCents(row.asking_price);
    const fee = toCents(row.api_fee == null || row.api_fee === '' ? '0' : row.api_fee);
    if (ask == null || fee == null) return;
    const total = ask + fee;
    if (lowest == null || total < lowest) lowest = total;
  });
  return lowest;
};

export const fetchRelistAskingPrice = (productVariantId, sizeId) => (
  apiClient().get('/admins/sells', {
    params: {
      product_variant_id: productVariantId,
      size_id: sizeId,
      sneakers_condition: 'BARU',
      box_condition: 'SEMPURNA',
      missing_accessories: 0,
      yellowing: 0,
      display_item: 0,
      sneakers_defect: 0,
      status: 'approved',
      has_quantity: 1,
      availables: 1,
      sort_by: 'askingPrice_asc',
      per_page: 20,
      without_append: 1,
    },
  }).then((response) => {
    const page = response.data && response.data.data;
    const rows = page && Array.isArray(page.data) ? page.data : [];
    return relistAskingPriceFromLowestCents(lowestAskCents(rows));
  })
);

export const defaultRelistAccount = (sale, accounts) => {
  const channel = ['shopee', 'tiktok'].includes(sale.created_source) ? sale.created_source : 'ka';
  return accounts.defaults[channel];
};

export const initialReturnRelistForm = (sale) => {
  const original = initialReturnQcForm(sale);
  const source = (sale.offer && sale.offer.user_sell) || {};
  const returnData = readSaleReturn(sale).data;
  const saved = returnData.form1 || {};
  const productId = saved.product_variant_id || (original.product && original.product.id);
  const sizeId = saved.size_id || (original.size && original.size.id);
  const sameProduct = original.product && Number(original.product.id) === Number(productId);
  const sameSize = sameProduct && original.size && Number(original.size.id) === Number(sizeId);
  const preview = returnData.relisting_defaults;
  const previewPrice = preview && Number(preview.product_variant_id) === Number(productId) && Number(preview.size_id) === Number(sizeId)
    ? String(preview.asking_price).replace(/\.0+$/, '') : '0';
  const defects = { ...original.defects, ...saved.defects };
  ['missing_accessories', 'yellowing', 'display_item', 'sneakers_defect'].forEach((key) => {
    if (saved[key] != null) defects[key] = saved[key] === true || saved[key] === 1 || saved[key] === '1';
  });
  const normalizeFlags = (flags) => Object.keys(flags).reduce((result, key) => ({
    ...result, [key]: flags[key] === true || flags[key] === 1 || flags[key] === '1',
  }), {});
  return {
    ...original,
    product: productId ? { ...original.product, id: productId, name: sameProduct ? original.product.name : '' } : null,
    size: sizeId ? { id: sizeId, US: sameSize ? original.size.US : '' } : null,
    sneakers_condition: saved.sneakers_condition || original.sneakers_condition,
    box_condition: saved.box_condition || original.box_condition,
    defects: normalizeFlags(defects),
    missing_accessories_list: normalizeFlags({ ...original.missing_accessories_list, ...saved.missing_accessories_list }),
    inclusions: normalizeFlags({ ...original.inclusions, ...saved.inclusions }),
    quantity: String((sale.offer && sale.offer.quantity) || 1),
    // The original sale price already includes API fee (see PaymentSummaries).
    // Preserve the transaction value; do not use the current listing price,
    // its previous purchase_price, seller proceeds, or add API fee again.
    purchase_price: sale.price == null ? '' : String(sale.price).replace(/\.0+$/, ''),
    asking_price: /^\d+$/.test(previewPrice) && previewPrice !== '0' ? previewPrice : '0',
    asking_price_loading: !(/^\d+$/.test(previewPrice) && previewPrice !== '0'),
    rack: null,
    source_invoice: sale.invoice_number || '',
    source_cg: source.consignment_id || (sale.sale_product && sale.sale_product.consignment_id) || '',
  };
};

export const returnRelistPayload = (form, ownerId) => {
  const condition = returnQcPayload(form);
  const result = {
    ...condition,
    // Existing BE accepts these four flags as top-level fields.
    missing_accessories: !!condition.defects.missing_accessories,
    yellowing: !!condition.defects.yellowing,
    display_item: !!condition.defects.display_item,
    sneakers_defect: !!condition.defects.sneakers_defect,
    user_id: ownerId,
    quantity: Number(form.quantity),
    purchase_price: form.purchase_price.trim() === '' ? null : form.purchase_price.trim(),
    rack_id: form.rack ? form.rack.id : null,
  };
  result.asking_price = form.asking_price.trim();
  return result;
};
